import { formatLastFirstName, formatReportDateYmd, resolveClientShortName, resolvePrimaryCareManagerName } from '../utils/reportPatientJoins';
import { currentYmKey, ymMinusOne, formatYmLabel } from '../utils/reportFormat';
import {
  ymdLocal,
  formatShortMdY,
  groupHoldAuditsByProgram,
  findActiveRpmAtAsOf,
} from './rpmCensusUtils';

/**
 * Resolve page-local as-of preset into census cutoff + selected month for New/Current.
 * @param {'now'|'beginCurrent'|'endCurrent'|'beginPrevious'|'endPrevious'|'custom'} preset
 * @param {string} [customYmd] YYYY-MM-DD when preset === 'custom'
 */
export function resolvePatientListingAsOf(preset, customYmd, now = new Date()) {
  const curYm = currentYmKey(now);
  const prevYm = ymMinusOne(curYm);

  const beginOfYm = (ym) => {
    const [y, m] = String(ym).split('-').map(Number);
    const start = new Date(y, m - 1, 1, 0, 0, 0, 0);
    return {
      mode: 'start',
      ymd: `${ym}-01`,
      cutoffDate: start,
      selectedYm: ym,
      label: `As of begin of ${formatYmLabel(ym)}`,
    };
  };

  const endOfYm = (ym) => {
    const [y, m] = String(ym).split('-').map(Number);
    const end = new Date(y, m, 0, 23, 59, 59, 999);
    const ymd = ymdLocal(end);
    return {
      mode: 'end',
      ymd,
      cutoffDate: end,
      selectedYm: ym,
      label: `As of end of ${formatYmLabel(ym)}`,
    };
  };

  const endOfDay = (ymd, label) => {
    const [y, m, d] = String(ymd).split('-').map(Number);
    if (!y || !m || !d) return null;
    const end = new Date(y, m - 1, d, 23, 59, 59, 999);
    const selectedYm = `${y}-${String(m).padStart(2, '0')}`;
    return {
      mode: 'end',
      ymd,
      cutoffDate: end,
      selectedYm,
      label: label || `As of ${formatShortMdY(ymd)}`,
    };
  };

  if (preset === 'beginCurrent') return beginOfYm(curYm);
  if (preset === 'endCurrent') return endOfYm(curYm);
  if (preset === 'beginPrevious') return beginOfYm(prevYm);
  if (preset === 'endPrevious') return endOfYm(prevYm);
  if (preset === 'custom') {
    const ymd = formatReportDateYmd(customYmd);
    return endOfDay(ymd) || endOfDay(ymdLocal(now), `As of now (${formatShortMdY(ymdLocal(now))})`);
  }

  // now (default)
  const today = ymdLocal(now);
  return endOfDay(today, `As of now (${formatShortMdY(today)})`);
}

function resolveLocation(patient, clientSites) {
  if (!patient?.clientSiteId) return '';
  const site = (clientSites || []).find((s) => s.id === patient.clientSiteId);
  return (site?.siteName || '').trim();
}

/**
 * Build Patient Listing of RPM-active patients at as-of.
 * @param {object} asOf from resolvePatientListingAsOf
 */
export function buildPatientListingReport({
  patients,
  programsByPatientId,
  holdAudits,
  clients,
  clientSites,
  userNameById,
  asOf,
}) {
  const empty = {
    kpis: { total: 0, newCount: 0, currentCount: 0 },
    rows: [],
    asOfLabel: asOf?.label || '',
  };

  if (!asOf?.ymd || !asOf?.cutoffDate || !Array.isArray(patients)) {
    return empty;
  }

  const auditsByProgram = groupHoldAuditsByProgram(holdAudits);
  const selectedYm = asOf.selectedYm || asOf.ymd.slice(0, 7);
  const rows = [];

  patients.forEach((patient) => {
    const patientId = patient.id;
    if (!patientId) return;
    const programs = programsByPatientId?.get?.(patientId) || [];
    const active = findActiveRpmAtAsOf(programs, auditsByProgram, patientId, asOf);
    if (!active?.session) return;

    const enrollYmd = formatReportDateYmd(active.session.enrollmentDate);
    const isNew = enrollYmd && enrollYmd.startsWith(`${selectedYm}-`);
    const status = isNew ? 'New' : 'Current';

    rows.push({
      _rowKey: patientId,
      patientId,
      status,
      statusRank: isNew ? 0 : 1,
      patientName: formatLastFirstName(patient) || patientId,
      mrn: patient.mrn || '',
      dob: formatShortMdY(patient.dateOfBirth),
      clientShortName: resolveClientShortName(patient, clients, clientSites),
      location: resolveLocation(patient, clientSites),
      careManagerName: resolvePrimaryCareManagerName(patient, userNameById),
      enrollmentDate: formatShortMdY(enrollYmd),
      enrollmentYmd: enrollYmd,
    });
  });

  rows.sort((a, b) => {
    if (a.statusRank !== b.statusRank) return a.statusRank - b.statusRank;
    return (a.patientName || '').localeCompare(b.patientName || '', undefined, { sensitivity: 'base' });
  });

  const newCount = rows.filter((r) => r.statusRank === 0).length;
  const currentCount = rows.length - newCount;

  return {
    kpis: {
      total: rows.length,
      newCount,
      currentCount,
    },
    rows,
    asOfLabel: asOf.label || '',
  };
}
